import { isPricingReplayErrorCode } from "@/modules/pos/domain/repo/sync-replay-errors";
import {
  getLocalOrder,
  markLocalCustomerDraftSynced,
  markLocalOrderSyncStatus,
  updateLocalOrderCustomerSnapshot,
} from "./db";
import { removeProvisionalLoyaltyRedemption } from "./loyalty-provisional";
import type { LocalOrderRecord } from "./types";

export { isCouponReplayErrorCode, isRewardReplayErrorCode, isPricingReplayErrorCode } from "@/modules/pos/domain/repo/sync-replay-errors";

type ReconcileLocalOrderAfterSyncInput = {
  serverOrderId?: string | null;
  replayResult?: {
    serverCustomerId?: string;
    couponReplay?: Array<{
      code: string;
      applied: boolean;
      errorCode?: string;
      error?: string;
    }>;
    rewardReplay?: {
      rewardId: string;
      applied: boolean;
      errorCode?: string;
      error?: string;
    };
  } | null;
  errorCode?: string | null;
  errorMessage?: string | null;
};

function buildCouponConflictMessage(
  replayResult: NonNullable<ReconcileLocalOrderAfterSyncInput["replayResult"]>,
): string | undefined {
  const failed = replayResult.couponReplay?.filter((entry) => !entry.applied);
  if (!failed?.length) return undefined;
  return failed
    .map(
      (entry) => `${entry.code}: ${entry.error ?? entry.errorCode ?? "failed"}`,
    )
    .join("; ");
}

function buildRewardConflictMessage(
  replayResult: NonNullable<ReconcileLocalOrderAfterSyncInput["replayResult"]>,
): string | undefined {
  const reward = replayResult.rewardReplay;
  if (!reward || reward.applied) return undefined;
  return `${reward.rewardId}: ${reward.error ?? reward.errorCode ?? "failed"}`;
}

export async function reconcileLocalOrderAfterSync(
  clientOrderId: string,
  input: ReconcileLocalOrderAfterSyncInput,
): Promise<LocalOrderRecord | undefined> {
  const order = await getLocalOrder(clientOrderId);
  if (!order) return undefined;

  const serverCustomerId =
    input.replayResult?.serverCustomerId ?? order.customerId;
  const localCustomerId = order.customerSnapshot?.localCustomerId;

  if (serverCustomerId && localCustomerId) {
    await markLocalCustomerDraftSynced(localCustomerId, serverCustomerId);
  }

  if (serverCustomerId) {
    await updateLocalOrderCustomerSnapshot(clientOrderId, {
      customerId: serverCustomerId,
      customerSnapshot: order.customerSnapshot
        ? {
            ...order.customerSnapshot,
            customerId: serverCustomerId,
            source:
              order.customerSnapshot.source === "local_draft"
                ? "server"
                : order.customerSnapshot.source,
          }
        : undefined,
      customerSyncStatus: "synced",
    });
  }

  const couponConflictMessage = input.replayResult
    ? buildCouponConflictMessage(input.replayResult)
    : undefined;
  const rewardConflictMessage = input.replayResult
    ? buildRewardConflictMessage(input.replayResult)
    : undefined;
  const pricingConflictMessage = [couponConflictMessage, rewardConflictMessage]
    .filter(Boolean)
    .join("; ");

  if (isPricingReplayErrorCode(input.errorCode) || pricingConflictMessage) {
    await markLocalOrderSyncStatus(clientOrderId, {
      syncStatus: "needs_review",
      serverOrderId: input.serverOrderId ?? order.serverOrderId,
      lastSyncError:
        input.errorMessage ??
        pricingConflictMessage ??
        `Pricing replay issue (${input.errorCode})`,
    });
    return await getLocalOrder(clientOrderId);
  }

  if (input.serverOrderId) {
    removeProvisionalLoyaltyRedemption(clientOrderId);
    await markLocalOrderSyncStatus(clientOrderId, {
      serverOrderId: input.serverOrderId,
    });
  }

  return await getLocalOrder(clientOrderId);
}
